import { useEffect, useState, useMemo, useRef } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Radio, FileText, Unlink, Download } from "lucide-react";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar } from "recharts";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { useConfirm } from "../components/ConfirmDialog";
import { useAppNames } from "../hooks/use-app-names";
import { EmployeeProgressSkeleton } from "../components/PageSkeletons";

const CAT_LABELS = { greeting: "Greeting", sop_adherence: "SOP adherence", tone: "Tone & courtesy", resolution: "Resolution" };
const HISTORY_PAGE_SIZE = 20;

export default function EmployeeProgress() {
  const { id } = useParams();
  const navigate = useNavigate();
  const confirm = useConfirm();
  const { appLabel } = useAppNames();
  const [data, setData] = useState(null);
  const [month, setMonth] = useState("");
  const [visibleCount, setVisibleCount] = useState(HISTORY_PAGE_SIZE);
  const [loadingMore, setLoadingMore] = useState(false);
  const sentinelRef = useRef(null);
  const isLoadingRef = useRef(false);

  const load = () => api.get(`/employees/${id}/scores`).then(({ data }) => { setData(data); setVisibleCount(HISTORY_PAGE_SIZE); }).catch(() => {});
  useEffect(() => { load(); }, [id]);

  const sortedSessions = useMemo(() => (data ? [...data.sessions].reverse() : []), [data]);
  const hasMoreHistory = visibleCount < sortedSessions.length;

  // Data for all sessions already arrives in one API call (see `sortedSessions`
  // above); this observer just paces how many rows get revealed, so scrolling a
  // long history stays cheap instead of mounting hundreds of rows at once.
  useEffect(() => {
    if (!hasMoreHistory) return;
    const node = sentinelRef.current;
    if (!node) return;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && !isLoadingRef.current) {
        isLoadingRef.current = true;
        setLoadingMore(true);
        setTimeout(() => {
          setVisibleCount((c) => Math.min(c + HISTORY_PAGE_SIZE, sortedSessions.length));
          setLoadingMore(false);
          isLoadingRef.current = false;
        }, 500);
      }
    }, { rootMargin: "150px" });
    observer.observe(node);
    return () => observer.disconnect();
  }, [hasMoreHistory, visibleCount, sortedSessions.length]);

  if (!data) return <EmployeeProgressSkeleton />;

  const { employee, sessions, summary } = data;
  const months = [...new Set(sessions.map((s) => s.date.slice(0, 7)))].reverse();
  const activeMonth = month || months[0];
  const monthLabel = (m) => new Date(`${m}-01T00:00:00`).toLocaleDateString(undefined, { month: "long", year: "numeric" });
  // session.date is only "YYYY-MM-DD" (no time-of-day) for sessions synced before/without
  // a full timestamp -- shown as date-only rather than a misleading "00:00". Once a full
  // ISO datetime is present ("T" in the string), the time is shown alongside it.
  const formatSessionDate = (d) => {
    if (!d.includes("T")) return new Date(`${d}T00:00:00`).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
    const parsed = new Date(d);
    if (isNaN(parsed.getTime())) return d;
    return parsed.toLocaleString(undefined, { year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
  };

  const downloadReport = () => {
    window.open(`${process.env.REACT_APP_BACKEND_URL}/api/employees/${id}/report?month=${activeMonth}`, "_blank");
  };

  const radarData = summary
    ? Object.entries(summary.categories).map(([k, v]) => ({ category: CAT_LABELS[k], score: v }))
    : [];

  const unassign = async () => {
    const ok = await confirm({
      title: "Unassign this pendant?",
      description: "The device mapping will be removed and the local server notified.",
      confirmLabel: "Unassign",
      danger: true,
    });
    if (!ok) return;
    try {
      await api.delete(`/employees/${id}/device`);
      toast.success("Pendant unassigned", { description: "Local server notified" });
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="space-y-8 fade-up">
      <div>
        <Link to="/console/employees" data-testid="back-to-employees" className="inline-flex items-center gap-1.5 text-sm text-neutral-500 hover:text-[#111] transition-colors">
          <ArrowLeft className="w-4 h-4" /> Employees
        </Link>
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mt-4">
          <div className="flex items-center gap-4 min-w-0">
            <div className="w-12 h-12 rounded-full bg-[#111] text-white flex items-center justify-center font-heading text-lg shrink-0">
              {employee.name.split(" ").map((w) => w[0]).slice(0, 2).join("")}
            </div>
            <div className="min-w-0">
              <h1 className="font-heading font-light text-3xl tracking-tight truncate" data-testid="employee-progress-name">{employee.name}</h1>
              <p className="text-sm text-neutral-500 truncate">{employee.position ? `${employee.position} · ` : ""}{employee.department}</p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {sessions.length > 0 && (
              <>
                <select data-testid="report-month-select" value={activeMonth} onChange={(e) => setMonth(e.target.value)}
                  className="bg-white border border-neutral-300 rounded-full px-3 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-[#111]">
                  {months.map((m) => <option key={m} value={m}>{monthLabel(m)}</option>)}
                </select>
                <button data-testid="download-report-button" onClick={downloadReport}
                  className="inline-flex items-center gap-1.5 bg-[#111] text-white rounded-full px-4 py-1.5 text-xs font-medium hover:bg-neutral-800 transition-colors">
                  <Download className="w-3.5 h-3.5" /> PDF report
                </button>
              </>
            )}
            {employee.device && (
              <>
                <span className="inline-flex items-center gap-1.5 text-xs font-mono-data bg-white border border-neutral-200 rounded-full px-3 py-1.5" data-testid="employee-device-badge">
                  <Radio className="w-3 h-3 text-green-600" /> {employee.device.serial_number} · {appLabel(employee.device.app_id)}
                </span>
                <button data-testid="unassign-device-button" onClick={unassign} title="Unassign pendant"
                  className="p-2 rounded-md text-neutral-400 hover:text-red-600 hover:bg-red-50 transition-colors">
                  <Unlink className="w-4 h-4" />
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {summary ? (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              ["Average score", summary.avg_overall, "over all sessions"],
              ["Best day", summary.best, "single-session peak"],
              [`${summary.trend_month} month`, `${summary.trend > 0 ? "+" : ""}${summary.trend}`, "first vs. latest week"],
              ["Interactions", summary.total_interactions, `${summary.total_sessions} evaluated sessions`],
            ].map(([label, value, sub], i) => (
              <div key={label} data-testid={`progress-kpi-${i}`} className={`rounded-lg border p-5 ${i === 0 ? "bg-[#111] text-white border-[#111]" : "bg-white border-neutral-200"}`}>
                <div className="font-heading text-2xl font-light">{value}</div>
                <div className={`text-xs mt-1 ${i === 0 ? "text-neutral-400" : "text-neutral-500"}`}>{label}</div>
                <div className={`text-[11px] mt-1.5 font-mono-data ${i === 0 ? "text-neutral-500" : "text-neutral-400"}`}>{sub}</div>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="lg:col-span-2 bg-white border border-neutral-200 rounded-lg p-6" data-testid="employee-trend-chart">
              <h2 className="text-sm font-medium">Daily score trend</h2>
              <div className="h-64 mt-4">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={sessions}>
                    <defs>
                      <linearGradient id="empFill" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#111111" stopOpacity={0.15} />
                        <stop offset="100%" stopColor="#111111" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#eeeeee" vertical={false} />
                    <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#999" }} tickFormatter={(d) => d.slice(5)} />
                    <YAxis domain={[40, 100]} tick={{ fontSize: 10, fill: "#999" }} width={30} />
                    <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid #e5e5e5" }} />
                    <Area type="monotone" dataKey="overall_score" name="Score" stroke="#111111" strokeWidth={1.5} fill="url(#empFill)" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>
            <div className="bg-white border border-neutral-200 rounded-lg p-6" data-testid="compliance-radar">
              <h2 className="text-sm font-medium">SOP compliance breakdown</h2>
              <div className="h-64 mt-4">
                <ResponsiveContainer width="100%" height="100%">
                  <RadarChart data={radarData} outerRadius="60%" margin={{ top: 12, right: 24, bottom: 12, left: 24 }}>
                    <PolarGrid stroke="#e5e5e5" />
                    <PolarAngleAxis dataKey="category" tick={{ fontSize: 10, fill: "#555" }} />
                    <PolarRadiusAxis domain={[0, 100]} tick={false} axisLine={false} />
                    <Radar dataKey="score" stroke="#111111" fill="#111111" fillOpacity={0.12} strokeWidth={1.5} />
                    <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid #e5e5e5" }} />
                  </RadarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          <div className="bg-white border border-neutral-200 rounded-lg" data-testid="session-history">
            <div className="px-6 py-4 border-b border-neutral-200 flex items-center justify-between">
              <h2 className="text-sm font-medium">Session history</h2>
              {employee.sop && (
                <span className="inline-flex items-center gap-1.5 text-xs text-neutral-500">
                  <FileText className="w-3.5 h-3.5" /> Evaluated against: {employee.sop.title} v{employee.sop.version}
                </span>
              )}
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm min-w-[640px]">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wide text-neutral-500 border-b border-neutral-200">
                    <th className="px-6 py-3 font-medium whitespace-nowrap">Date</th>
                    <th className="px-4 py-3 font-medium whitespace-nowrap">Overall</th>
                    {Object.values(CAT_LABELS).map((l) => <th key={l} className="px-4 py-3 font-medium whitespace-nowrap">{l}</th>)}
                    <th className="px-4 py-3 font-medium whitespace-nowrap">Interactions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-neutral-100">
                  {sortedSessions.slice(0, visibleCount).map((s) => (
                    <tr key={s.id} onClick={() => navigate(`/console/employees/${id}/sessions/${s.id}`)}
                      className="hover:bg-neutral-50 transition-colors cursor-pointer">
                      <td className="px-6 py-3 font-mono-data text-xs text-neutral-600 whitespace-nowrap">{formatSessionDate(s.date)}</td>
                      <td className="px-4 py-3 font-heading">{s.overall_score}</td>
                      {Object.keys(CAT_LABELS).map((k) => <td key={k} className="px-4 py-3 text-neutral-500">{s.categories[k]}</td>)}
                      <td className="px-4 py-3 text-neutral-500">{s.session_count}</td>
                    </tr>
                  ))}
                  {loadingMore && (
                    <tr>
                      <td colSpan={7} className="px-6 py-4 text-center">
                        <span className="inline-flex items-center gap-2 text-xs text-neutral-400">
                          <span className="w-3.5 h-3.5 border-2 border-neutral-300 border-t-neutral-600 rounded-full animate-spin" />
                          Loading more…
                        </span>
                      </td>
                    </tr>
                  )}
                  {hasMoreHistory && !loadingMore && (
                    <tr>
                      <td colSpan={7} className="p-0"><div ref={sentinelRef} className="h-px" /></td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      ) : (
        <div className="bg-white border border-neutral-200 rounded-lg p-12 text-center" data-testid="no-scores-state">
          <p className="text-sm text-neutral-500">No scores yet. Scores appear here once the local server processes this employee's sessions.</p>
        </div>
      )}
    </div>
  );
}
